import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Users2, Coffee, Plane, Plus, X, Play, Pause, Power, Clock, BarChart3, AlertCircle, Pencil, Download, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

type Praesenz = 'aktiv' | 'pause' | 'inaktiv';

const ANDREAS_ID = '11111111-1111-1111-1111-111111111111';
const ANDREAS_EMAIL = 'andila111@googlemail.com';
const BOT_IDS = new Set([
  '00000000-0000-0000-0000-000000000099',
  '16edc88f-1de6-4690-9d62-51ff798e0fd3',
  '212ed0b7-ba4a-4357-aca0-7e935c95cba8',
  '9ddb6874-800d-4cfa-b486-d2464971dda5',
]);

const WT_KURZ = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

interface PlanRow {
  mitarbeiter_id: string;
  wochentag: number;
  arbeitet: boolean;
  von: string;
  bis: string;
  pause_minuten: number;
}
interface UrlaubRow {
  id: string;
  mitarbeiter_id: string;
  von_datum: string;
  bis_datum: string;
  art: string;
  notiz?: string | null;
}
interface Session {
  id: string;
  mitarbeiter_id: string;
  typ: string;
  beginn: string;
  ende: string | null;
  dauer_minuten: number | null;
  datum: string;
  quelle?: string | null;
  grund?: string | null;
}
type PlanMap = Record<string, Record<number, PlanRow>>;

// ---- Helfer ----
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function wtOf(d: Date): number {
  const j = d.getDay();
  return j === 0 ? 7 : j;
}
function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function mondayOf(d: Date): Date {
  const c = new Date(d);
  c.setDate(c.getDate() - (wtOf(c) - 1));
  return c;
}
function daysInMonth(y: number, m0: number): number {
  return new Date(y, m0 + 1, 0).getDate();
}
function parseHM(t: string): number {
  if (!t) return 0;
  const p = t.split(':');
  return Number(p[0]) + Number(p[1] || 0) / 60;
}
function sollHours(p?: { arbeitet: boolean; von: string; bis: string; pause_minuten: number }): number {
  if (!p || !p.arbeitet) return 0;
  const h = parseHM(p.bis) - parseHM(p.von) - (p.pause_minuten || 0) / 60;
  return Math.max(0, Math.round(h * 100) / 100);
}
function fmtH(n: number): string {
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}
function fmtDauer(min: number): string {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h <= 0) return `${r} Min`;
  return `${h} Std ${r} Min`;
}
function fmtSaldo(min: number): string {
  const sign = min >= 0 ? '+' : '−';
  return `${sign}${fmtDauer(Math.abs(min))}`;
}
function fmtHMS(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
function fmtUhr(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function fmtDatum(s: string): string {
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}
function artLabel(a: string): string {
  return a === 'krank' ? 'Krank' : a === 'frei' ? 'Frei' : a === 'sonstiges' ? 'Sonstiges' : 'Urlaub';
}
function defaultPlan(id: string): PlanRow[] {
  return [1, 2, 3, 4, 5, 6, 7].map((wt) => ({
    mitarbeiter_id: id,
    wochentag: wt,
    arbeitet: wt <= 5,
    von: '08:00',
    bis: '17:00',
    pause_minuten: 30,
  }));
}
// Feiertage NRW (inkl. beweglicher Feste über Ostern)
function easterSunday(y: number): Date {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(y, month - 1, day);
}
function holidaysNRW(y: number): string[] {
  const out: string[] = [];
  out.push(ymd(new Date(y, 0, 1)));   // Neujahr
  out.push(ymd(new Date(y, 4, 1)));   // Tag der Arbeit
  out.push(ymd(new Date(y, 9, 3)));   // Tag der Deutschen Einheit
  out.push(ymd(new Date(y, 10, 1)));  // Allerheiligen
  out.push(ymd(new Date(y, 11, 25))); // 1. Weihnachtstag
  out.push(ymd(new Date(y, 11, 26))); // 2. Weihnachtstag
  const e = easterSunday(y);
  out.push(ymd(addDays(e, -2)));  // Karfreitag
  out.push(ymd(addDays(e, 1)));   // Ostermontag
  out.push(ymd(addDays(e, 39)));  // Christi Himmelfahrt
  out.push(ymd(addDays(e, 50)));  // Pfingstmontag
  out.push(ymd(addDays(e, 60)));  // Fronleichnam
  return out;
}

const dotColor = (s: Praesenz) =>
  s === 'aktiv' ? 'bg-green-500' : s === 'pause' ? 'bg-amber-500' : 'bg-gray-300';
const statusLabel = (s: Praesenz) => (s === 'aktiv' ? 'Aktiv' : s === 'pause' ? 'Pause' : 'Inaktiv');
const ampel = (saldoMin: number) =>
  saldoMin >= -15 ? 'text-green-600' : saldoMin >= -120 ? 'text-amber-600' : 'text-red-600';

export default function MitarbeiterMenu() {
  const { profile, teamMembers, onlineUserIds } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'heute' | 'team' | 'woche' | 'urlaub' | 'auswertung'>('heute');
  const ref = useRef<HTMLDivElement>(null);

  const [nowTs, setNowTs] = useState(Date.now());
  const [myStatus, setMyStatus] = useState<Praesenz>('inaktiv');
  const [praesenz, setPraesenz] = useState<Record<string, Praesenz>>({});
  const [praesenzSeit, setPraesenzSeit] = useState<Record<string, string>>({});
  const [myPlan, setMyPlan] = useState<PlanRow[]>([]);
  const [todayPlans, setTodayPlans] = useState<Record<string, PlanRow>>({});
  const [urlaub, setUrlaub] = useState<UrlaubRow[]>([]);
  const [todaySessions, setTodaySessions] = useState<Session[]>([]);
  const [weekSessions, setWeekSessions] = useState<Session[]>([]);
  const [openSession, setOpenSession] = useState<Session | null>(null);

  // Nachtragen
  const [ntOpen, setNtOpen] = useState(false);
  const [ntDatum, setNtDatum] = useState('');
  const [ntVon, setNtVon] = useState('');
  const [ntBis, setNtBis] = useState('');
  const [ntTyp, setNtTyp] = useState<'arbeit' | 'pause'>('arbeit');
  const [ntGrund, setNtGrund] = useState('');

  // Urlaub-Formular
  const [vonU, setVonU] = useState('');
  const [bisU, setBisU] = useState('');
  const [artU, setArtU] = useState('urlaub');
  const [urlaubView, setUrlaubView] = useState<'meine' | 'team'>('meine');

  // Auswertung
  const [augRange, setAugRange] = useState<'heute' | 'woche' | 'monat'>('woche');
  const [augData, setAugData] = useState<Session[]>([]);
  const [augPlans, setAugPlans] = useState<PlanMap>({});
  const [selMember, setSelMember] = useState<string | null>(null);

  const meId = profile?.id;
  const isAndreas = profile?.id === ANDREAS_ID || (profile?.email || '').toLowerCase() === ANDREAS_EMAIL;
  const today = new Date();
  const jahr = today.getFullYear();

  const holidays = useMemo(() => {
    const s = new Set<string>();
    [jahr - 1, jahr, jahr + 1].forEach((y) => holidaysNRW(y).forEach((d) => s.add(d)));
    return s;
  }, [jahr]);

  useEffect(() => {
    if (!open) return;
    const iv = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [open]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const loadAll = useCallback(async () => {
    if (!meId) return;
    const t = ymd(new Date());
    const wt = wtOf(new Date());
    const wkStart = ymd(mondayOf(new Date()));
    try {
      const [planRes, todayRes, urlRes, prRes, sessRes, weekRes, openRes] = await Promise.all([
        supabase.from('mitarbeiter_wochenplan').select('*').eq('mitarbeiter_id', meId).order('wochentag'),
        supabase.from('mitarbeiter_wochenplan').select('*').eq('wochentag', wt),
        supabase.from('mitarbeiter_urlaub').select('*').order('von_datum'),
        supabase.from('mitarbeiter_praesenz').select('*'),
        supabase.from('mitarbeiter_zeiterfassung').select('*').eq('mitarbeiter_id', meId).eq('datum', t).order('beginn'),
        supabase.from('mitarbeiter_zeiterfassung').select('*').eq('mitarbeiter_id', meId).gte('datum', wkStart).lte('datum', t).order('beginn'),
        supabase.from('mitarbeiter_zeiterfassung').select('*').eq('mitarbeiter_id', meId).is('ende', null).order('beginn', { ascending: false }).limit(1),
      ]);
      const mine = (planRes.data as PlanRow[]) || [];
      setMyPlan(mine.length ? mine : defaultPlan(meId));
      const tp: Record<string, PlanRow> = {};
      ((todayRes.data as PlanRow[]) || []).forEach((r) => { tp[r.mitarbeiter_id] = r; });
      setTodayPlans(tp);
      setUrlaub((urlRes.data as UrlaubRow[]) || []);
      const pr: Record<string, Praesenz> = {};
      const ps: Record<string, string> = {};
      ((prRes.data as { mitarbeiter_id: string; status: Praesenz; seit: string }[]) || []).forEach((r) => {
        pr[r.mitarbeiter_id] = r.status; ps[r.mitarbeiter_id] = r.seit;
      });
      setPraesenz(pr); setPraesenzSeit(ps);
      setMyStatus(pr[meId] || 'inaktiv');
      setTodaySessions((sessRes.data as Session[]) || []);
      setWeekSessions((weekRes.data as Session[]) || []);
      setOpenSession(((openRes.data as Session[]) || [])[0] || null);
    } catch (e) {
      console.error('MitarbeiterMenu load', e);
    }
  }, [meId]);

  useEffect(() => { if (open) loadAll(); }, [open, loadAll]);

  // Echtzeit: Team-Präsenz live aktualisieren
  useEffect(() => {
    if (!open) return;
    const ch = supabase
      .channel('praesenz-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'mitarbeiter_praesenz' }, (payload) => {
        const row = payload.new as { mitarbeiter_id?: string; status?: Praesenz; seit?: string } | null;
        if (row && row.mitarbeiter_id) {
          setPraesenz((p) => ({ ...p, [row.mitarbeiter_id as string]: (row.status || 'inaktiv') as Praesenz }));
          if (row.seit) setPraesenzSeit((p) => ({ ...p, [row.mitarbeiter_id as string]: row.seit as string }));
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [open]);

  const loadAuswertung = useCallback(async () => {
    if (!isAndreas) return;
    const from =
      augRange === 'heute' ? ymd(today)
      : augRange === 'woche' ? ymd(mondayOf(today))
      : ymd(new Date(today.getFullYear(), today.getMonth(), 1));
    const [planRes, sessRes] = await Promise.all([
      supabase.from('mitarbeiter_wochenplan').select('*'),
      supabase.from('mitarbeiter_zeiterfassung').select('*').gte('datum', from).lte('datum', ymd(today)).order('beginn'),
    ]);
    const pm: PlanMap = {};
    ((planRes.data as PlanRow[]) || []).forEach((r) => {
      (pm[r.mitarbeiter_id] ||= {})[r.wochentag] = r;
    });
    setAugPlans(pm);
    setAugData((sessRes.data as Session[]) || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAndreas, augRange]);

  useEffect(() => {
    if (open && tab === 'auswertung' && isAndreas) loadAuswertung();
  }, [open, tab, augRange, isAndreas, loadAuswertung]);

  // ---- Stempeln ----
  async function stamp(newStatus: Praesenz) {
    if (!meId) return;
    const nowIso = new Date().toISOString();
    try {
      let sessions = [...todaySessions];
      if (openSession) {
        const dauer = (Date.now() - new Date(openSession.beginn).getTime()) / 60000;
        await supabase.from('mitarbeiter_zeiterfassung').update({ ende: nowIso, dauer_minuten: Math.round(dauer * 100) / 100 }).eq('id', openSession.id);
        sessions = sessions.map((s) => (s.id === openSession.id ? { ...s, ende: nowIso, dauer_minuten: Math.round(dauer * 100) / 100 } : s));
      }
      let newOpen: Session | null = null;
      if (newStatus === 'aktiv' || newStatus === 'pause') {
        const typ = newStatus === 'aktiv' ? 'arbeit' : 'pause';
        const { data, error } = await supabase
          .from('mitarbeiter_zeiterfassung')
          .insert({ mitarbeiter_id: meId, typ, beginn: nowIso, datum: ymd(new Date()), quelle: 'stempel' })
          .select().single();
        if (error) throw error;
        newOpen = data as Session;
        if (newOpen.datum === ymd(new Date())) sessions = [...sessions, newOpen];
      }
      setOpenSession(newOpen);
      setTodaySessions(sessions);
      setMyStatus(newStatus);
      setPraesenz((p) => ({ ...p, [meId]: newStatus }));
      setPraesenzSeit((p) => ({ ...p, [meId]: nowIso }));
      await supabase.from('mitarbeiter_praesenz').upsert({ mitarbeiter_id: meId, status: newStatus, seit: nowIso, updated_at: nowIso }, { onConflict: 'mitarbeiter_id' });
      if (newStatus === 'aktiv') toast.success('Eingestempelt — deine Zeit läuft 👍');
      else if (newStatus === 'pause') toast.success('Pause — Zeit pausiert');
      else toast.success('Ausgestempelt — schönen Feierabend!');
    } catch (e) {
      console.error(e);
      toast.error('Stempeln fehlgeschlagen');
    }
  }

  async function submitNachtrag() {
    if (!meId) return;
    if (!ntDatum || !ntVon || !ntBis) { toast.error('Bitte Datum und Zeiten angeben'); return; }
    if (ntBis <= ntVon) { toast.error('„Bis" muss nach „Von" liegen'); return; }
    if (!ntGrund.trim()) { toast.error('Bitte einen Grund angeben'); return; }
    const beginnIso = new Date(`${ntDatum}T${ntVon}:00`).toISOString();
    const endeIso = new Date(`${ntDatum}T${ntBis}:00`).toISOString();
    const dauer = (new Date(endeIso).getTime() - new Date(beginnIso).getTime()) / 60000;
    const { data, error } = await supabase
      .from('mitarbeiter_zeiterfassung')
      .insert({ mitarbeiter_id: meId, typ: ntTyp, beginn: beginnIso, ende: endeIso, dauer_minuten: Math.round(dauer * 100) / 100, datum: ntDatum, quelle: 'nachtrag', grund: ntGrund.trim(), erfasst_von: meId })
      .select().single();
    if (error) { toast.error('Nachtrag fehlgeschlagen'); return; }
    await supabase.from('zeiterfassung_audit').insert({ session_id: (data as Session).id, mitarbeiter_id: meId, aktion: 'nachtrag', neu: data, grund: ntGrund.trim(), von_user: meId });
    if (ntDatum === ymd(new Date())) setTodaySessions((prev) => [...prev, data as Session].sort((a, b) => a.beginn.localeCompare(b.beginn)));
    toast.success('Nachgetragen — danke!');
    setNtOpen(false); setNtVon(''); setNtBis(''); setNtGrund('');
    if (tab === 'auswertung' && isAndreas) loadAuswertung();
  }

  function patchPlan(wt: number, patch: Partial<PlanRow>) {
    setMyPlan((prev) => prev.map((r) => (r.wochentag === wt ? { ...r, ...patch } : r)));
  }
  async function savePlan() {
    if (!meId) return;
    const rows = myPlan.map((r) => ({
      mitarbeiter_id: meId, wochentag: r.wochentag, arbeitet: r.arbeitet,
      von: r.von.slice(0, 5), bis: r.bis.slice(0, 5), pause_minuten: Number(r.pause_minuten) || 0,
    }));
    const { error } = await supabase.from('mitarbeiter_wochenplan').upsert(rows, { onConflict: 'mitarbeiter_id,wochentag' });
    if (error) { toast.error('Wochenplan nicht gespeichert'); return; }
    toast.success('Wochenplan gespeichert');
  }

  async function addUrlaub() {
    if (!meId || !vonU || !bisU) { toast.error('Bitte Zeitraum wählen'); return; }
    if (bisU < vonU) { toast.error('Bis-Datum liegt vor Von-Datum'); return; }
    const { data, error } = await supabase.from('mitarbeiter_urlaub').insert({ mitarbeiter_id: meId, von_datum: vonU, bis_datum: bisU, art: artU }).select().single();
    if (error) { toast.error('Urlaub nicht gespeichert'); return; }
    setUrlaub((prev) => [...prev, data as UrlaubRow].sort((a, b) => a.von_datum.localeCompare(b.von_datum)));
    // Hinweis bei Überschneidung mit Kollegen
    const clash = urlaub.filter((u) => u.mitarbeiter_id !== meId && u.art !== 'sonstiges' && !(u.bis_datum < vonU || u.von_datum > bisU));
    if (clash.length) {
      const namen = [...new Set(clash.map((c) => nameOf(c.mitarbeiter_id)))].join(', ');
      toast('Achtung: Überschneidung mit ' + namen, { description: 'Nur ein Hinweis — eingetragen ist es trotzdem.', icon: '⚠️' });
    } else {
      toast.success('Eingetragen');
    }
    setVonU(''); setBisU('');
  }
  async function delUrlaub(id: string) {
    const { error } = await supabase.from('mitarbeiter_urlaub').delete().eq('id', id);
    if (error) { toast.error('Konnte nicht löschen'); return; }
    setUrlaub((prev) => prev.filter((u) => u.id !== id));
  }

  // ---- abgeleitete Werte ----
  const members = (teamMembers || []).filter((m) => m.ist_aktiv && !BOT_IDS.has(m.id) && m.rolle !== 'ki_agent');
  const nameOf = (id: string) => {
    const m = members.find((x) => x.id === id) || (teamMembers || []).find((x) => x.id === id);
    return m ? `${m.vorname} ${m.nachname}` : id.slice(0, 8);
  };

  function urlaubAm(id: string, dstr: string): UrlaubRow | undefined {
    return urlaub.find((u) => u.mitarbeiter_id === id && u.von_datum <= dstr && u.bis_datum >= dstr);
  }
  // Team-Urlaub: Farbe je Mitarbeiter + Überschneidungen
  const MEMBER_COLORS = ['#7c3aed', '#0891b2', '#ea580c', '#16a34a', '#db2777', '#2563eb', '#ca8a04', '#dc2626', '#0d9488', '#9333ea', '#4f46e5'];
  const colorFor = (id: string) => {
    const i = members.findIndex((m) => m.id === id);
    return MEMBER_COLORS[(i < 0 ? 0 : i) % MEMBER_COLORS.length];
  };
  function urlaubMembersAm(dstr: string): UrlaubRow[] {
    return urlaub.filter((u) => u.art !== 'sonstiges' && u.von_datum <= dstr && u.bis_datum >= dstr);
  }
  const overlapTage = useMemo(() => {
    let n = 0;
    for (let m0 = 0; m0 < 12; m0++) {
      const dim = daysInMonth(jahr, m0);
      for (let i = 1; i <= dim; i++) {
        const ds = ymd(new Date(jahr, m0, i));
        const ids = new Set(urlaub.filter((u) => u.art !== 'sonstiges' && u.von_datum <= ds && u.bis_datum >= ds).map((u) => u.mitarbeiter_id));
        if (ids.size > 1) n++;
      }
    }
    return n;
  }, [urlaub, jahr]);
  const teamUrlaub = urlaub
    .filter((u) => u.bis_datum >= ymd(today))
    .sort((a, b) => a.von_datum.localeCompare(b.von_datum));
  function scheduledNow(id: string): boolean {
    const p = todayPlans[id];
    if (!p || !p.arbeitet) return false;
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    return h >= parseHM(p.von) && h < parseHM(p.bis);
  }
  function sinceText(iso?: string): string {
    if (!iso) return '';
    const min = (nowTs - new Date(iso).getTime()) / 60000;
    if (min < 1) return 'gerade eben';
    return fmtDauer(min);
  }
  // Soll-Stunden eines Tages (0 an Feiertagen / bei Urlaub/Krank)
  function sollForDate(planForMember: Record<number, PlanRow> | undefined, memberId: string, dstr: string): number {
    if (holidays.has(dstr)) return 0;
    const u = urlaub.find((x) => x.mitarbeiter_id === memberId && x.von_datum <= dstr && x.bis_datum >= dstr);
    if (u && (u.art === 'urlaub' || u.art === 'krank' || u.art === 'frei')) return 0;
    const d = new Date(dstr + 'T00:00:00');
    return sollHours(planForMember?.[wtOf(d)]);
  }
  function sessionMin(s: Session): number {
    return s.dauer_minuten ?? (s.ende ? 0 : (nowTs - new Date(s.beginn).getTime()) / 60000);
  }

  // heutige Summen
  function sessionSec(s: Session): number {
    return s.dauer_minuten != null ? s.dauer_minuten * 60 : (s.ende ? 0 : (nowTs - new Date(s.beginn).getTime()) / 1000);
  }
  const arbeitSec = todaySessions.filter((s) => s.typ === 'arbeit').reduce((a, s) => a + sessionSec(s), 0);
  const pauseSec = todaySessions.filter((s) => s.typ === 'pause').reduce((a, s) => a + sessionSec(s), 0);
  const sollHeute = sollHours(todayPlans[meId || '']);
  const staleOpen = openSession && openSession.datum < ymd(today);
  const runSec = openSession ? (nowTs - new Date(openSession.beginn).getTime()) / 1000 : 0;
  const nowDate = new Date(nowTs);
  const nowClock = nowDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const nowDateLabel = nowDate.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });
  // Hinweise
  const longNoPause = myStatus === 'aktiv' && arbeitSec >= 6 * 3600 && pauseSec < 30 * 60;
  const longPause = myStatus === 'pause' && runSec > 60 * 60;

  // Selbst-Wochensaldo
  const myPlanMap = useMemo(() => {
    const m: Record<number, PlanRow> = {};
    myPlan.forEach((r) => { m[r.wochentag] = r; });
    return m;
  }, [myPlan]);
  const weekSaldo = (() => {
    // erst ab dem ersten erfassten Tag dieser Woche zählen (sonst wirkt der Saldo grundlos stark negativ)
    const firstDatum = weekSessions.length ? weekSessions.reduce((a, s) => (a < s.datum ? a : s.datum), weekSessions[0].datum) : null;
    if (!firstDatum) return { ist: 0, soll: 0, saldo: 0, started: false };
    let ist = 0, soll = 0;
    for (let d = mondayOf(today); d <= today; d = addDays(d, 1)) {
      const ds = ymd(d);
      if (ds < firstDatum) continue;
      ist += weekSessions.filter((s) => s.typ === 'arbeit' && s.datum === ds).reduce((a, s) => a + sessionMin(s), 0);
      soll += sollForDate(myPlanMap, meId || '', ds) * 60;
    }
    return { ist, soll, saldo: ist - soll, started: true };
  })();

  const myUrlaub = urlaub.filter((u) => u.mitarbeiter_id === meId);
  const urlaubstageJahr = myUrlaub.filter((u) => u.art === 'urlaub').reduce((acc, u) => {
    let c = 0;
    let d = new Date(u.von_datum + 'T00:00:00');
    const end = new Date(u.bis_datum + 'T00:00:00');
    while (d <= end) { if (d.getFullYear() === jahr && wtOf(d) <= 5 && !holidays.has(ymd(d))) c++; d = addDays(d, 1); }
    return acc + c;
  }, 0);

  // Auswertung: pro Mitarbeiter aufbereiten
  const augFrom =
    augRange === 'heute' ? ymd(today)
    : augRange === 'woche' ? ymd(mondayOf(today))
    : ymd(new Date(today.getFullYear(), today.getMonth(), 1));
  function rangeDays(): string[] {
    const out: string[] = [];
    for (let d = new Date(augFrom + 'T00:00:00'); ymd(d) <= ymd(today); d = addDays(d, 1)) out.push(ymd(d));
    return out;
  }
  function memberStats(id: string) {
    const days = rangeDays();
    let ist = 0, soll = 0;
    const rows = days.map((ds) => {
      const arb = augData.filter((s) => s.mitarbeiter_id === id && s.typ === 'arbeit' && s.datum === ds);
      const pau = augData.filter((s) => s.mitarbeiter_id === id && s.typ === 'pause' && s.datum === ds);
      const dIst = arb.reduce((a, s) => a + sessionMin(s), 0);
      const dPause = pau.reduce((a, s) => a + sessionMin(s), 0);
      const dSoll = sollForDate(augPlans[id], id, ds) * 60;
      ist += dIst; soll += dSoll;
      const kommen = arb.length ? arb.reduce((a, s) => (a < s.beginn ? a : s.beginn), arb[0].beginn) : null;
      const gehenArr = arb.filter((s) => s.ende);
      const gehen = gehenArr.length ? gehenArr.reduce((a, s) => (a > (s.ende as string) ? a : (s.ende as string)), gehenArr[0].ende as string) : null;
      const nachtrag = arb.some((s) => s.quelle === 'nachtrag') || pau.some((s) => s.quelle === 'nachtrag');
      const grund = (arb.find((s) => s.grund) || pau.find((s) => s.grund))?.grund || null;
      return { ds, dIst, dPause, dSoll, kommen, gehen, nachtrag, grund };
    }).filter((r) => r.dIst > 0 || r.dSoll > 0);
    return { ist, soll, saldo: ist - soll, rows };
  }

  function exportCSV() {
    const sep = ';';
    const head = ['Mitarbeiter', 'Datum', 'Kommen', 'Gehen', 'Pause (Min)', 'Ist (Std)', 'Soll (Std)', 'Saldo (Std)', 'Quelle', 'Grund'];
    const lines = [head.join(sep)];
    members.forEach((m) => {
      const st = memberStats(m.id);
      st.rows.forEach((r) => {
        lines.push([
          nameOf(m.id),
          r.ds,
          r.kommen ? fmtUhr(r.kommen) : '',
          r.gehen ? fmtUhr(r.gehen) : '',
          Math.round(r.dPause).toString(),
          (r.dIst / 60).toFixed(2).replace('.', ','),
          (r.dSoll / 60).toFixed(2).replace('.', ','),
          ((r.dIst - r.dSoll) / 60).toFixed(2).replace('.', ','),
          r.nachtrag ? 'Nachtrag' : 'Stempel',
          (r.grund || '').replace(/[\r\n;]+/g, ' '),
        ].join(sep));
      });
    });
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeiterfassung_${augRange}_${ymd(today)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV exportiert');
  }

  const pillCls =
    myStatus === 'aktiv' ? 'bg-green-50 text-green-700 hover:bg-green-100'
    : myStatus === 'pause' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
    : 'bg-gray-100 text-gray-500 hover:bg-gray-200';

  const tabs: [typeof tab, string][] = [
    ['heute', 'Heute'], ['team', 'Team'], ['woche', 'Woche'], ['urlaub', 'Urlaub'],
  ];
  if (isAndreas) tabs.push(['auswertung', 'Auswertung']);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          setOpen((o) => !o);
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission();
          } catch { /* ignore */ }
        }}
        className={`flex items-center gap-1.5 px-2 md:px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${pillCls}`}
        title="Team & Zeiterfassung"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor(myStatus)} ${myStatus === 'aktiv' ? 'animate-pulse' : ''}`} />
        <span className="hidden sm:inline">{statusLabel(myStatus)}</span>
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-16 max-h-[82vh] md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-[520px] md:max-h-[88vh] overflow-hidden bg-white rounded-2xl shadow-2xl border border-gray-200 z-[60] flex flex-col">
          <div className="px-4 pt-3.5 pb-2 border-b border-gray-100 flex items-center gap-2">
            <Users2 className="w-[18px] h-[18px] text-gray-500" />
            <span className="text-[15px] font-semibold text-gray-900">Team &amp; Zeiterfassung</span>
          </div>

          <div className="flex items-center gap-1 px-3 pt-2.5">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${tab === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'} ${k === 'auswertung' ? 'ml-auto' : ''}`}>
                {l}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto px-3 py-3">
            {/* ===== HEUTE ===== */}
            {tab === 'heute' && (
              <div className="space-y-3">
                {staleOpen && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-[13px] text-amber-900 font-medium">Du bist noch von gestern eingestempelt.</p>
                      <p className="text-[12px] text-amber-700">Seit {fmtUhr(openSession!.beginn)} ({fmtDatum(openSession!.datum)}). Bitte ausstempeln.</p>
                      <button onClick={() => stamp('inaktiv')} className="mt-1.5 px-2.5 py-1 rounded-md bg-amber-600 text-white text-[12px] font-medium">Jetzt ausstempeln</button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between px-0.5">
                  <span className="text-[12px] text-gray-400 capitalize">{nowDateLabel}</span>
                  <span className="text-[13px] font-semibold text-gray-700 tabular-nums">{nowClock}</span>
                </div>

                {myStatus === 'inaktiv' ? (
                  <button onClick={() => stamp('aktiv')} className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-[16px] font-semibold transition-colors">
                    <Play className="w-5 h-5" /> Einstempeln
                  </button>
                ) : myStatus === 'aktiv' ? (
                  <div className="p-4 rounded-2xl bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-[13px] font-medium">Eingestempelt · läuft</span>
                      <span className="ml-auto text-[12px] text-green-600">seit {openSession ? fmtUhr(openSession.beginn) : '—'}</span>
                    </div>
                    <div className="text-center my-2">
                      <span className="text-[38px] leading-none font-bold text-green-700 tabular-nums tracking-tight">{fmtHMS(runSec)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => stamp('pause')} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white border border-amber-300 text-amber-700 text-[14px] font-semibold hover:bg-amber-50"><Pause className="w-4 h-4" />Pause</button>
                      <button onClick={() => stamp('inaktiv')} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-900 text-white text-[14px] font-semibold hover:bg-gray-800"><Power className="w-4 h-4" />Feierabend</button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Pause className="w-3.5 h-3.5" />
                      <span className="text-[13px] font-medium">Pause läuft — Zeit pausiert</span>
                      <span className="ml-auto text-[12px] text-amber-600">seit {openSession ? fmtUhr(openSession.beginn) : '—'}</span>
                    </div>
                    <div className="text-center my-2">
                      <span className="text-[38px] leading-none font-bold text-amber-600 tabular-nums tracking-tight">{fmtHMS(runSec)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => stamp('aktiv')} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-green-600 text-white text-[14px] font-semibold hover:bg-green-700"><Play className="w-4 h-4" />Weiter arbeiten</button>
                      <button onClick={() => stamp('inaktiv')} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 text-[14px] font-semibold hover:bg-gray-50"><Power className="w-4 h-4" />Feierabend</button>
                    </div>
                  </div>
                )}

                {(longNoPause || longPause) && (
                  <div className="space-y-1">
                    {longNoPause && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100 text-[12px] text-amber-800">
                        <Coffee className="w-3.5 h-3.5 shrink-0" /> Du arbeitest seit über 6 Std — Zeit für eine Pause (ab 6 Std vorgeschrieben).
                      </div>
                    )}
                    {longPause && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100 text-[12px] text-amber-800">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Lange Pause ({fmtDauer(runSec / 60)}) — nicht vergessen, dich wieder einzustempeln.
                      </div>
                    )}
                  </div>
                )}

                <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className={`w-4 h-4 ${myStatus === 'aktiv' ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-[13px] text-gray-500">Heute gearbeitet</span>
                    </div>
                    <span className="text-[22px] font-bold text-gray-900 tabular-nums">{fmtHMS(arbeitSec)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[12px] text-gray-400">
                    <span>Pause heute: {fmtHMS(pauseSec)}</span>
                    <span>{sollHeute > 0 ? `Soll: ${fmtH(sollHeute)} Std` : 'heute frei'}</span>
                  </div>
                  {weekSaldo.started && (
                    <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-[12px]">
                      <span className="text-gray-500">Diese Woche</span>
                      <span className="text-gray-500">Ist {fmtDauer(weekSaldo.ist)} · Soll {fmtDauer(weekSaldo.soll)} · <span className={`font-semibold ${ampel(weekSaldo.saldo)}`}>{fmtSaldo(weekSaldo.saldo)}</span></span>
                    </div>
                  )}
                </div>

                {todaySessions.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[11px] font-medium text-gray-400 px-1">Heutige Stempelungen</p>
                    {todaySessions.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-[13px]">
                        <span className={`w-1.5 h-1.5 rounded-full ${s.typ === 'arbeit' ? 'bg-green-500' : 'bg-amber-500'}`} />
                        <span className="text-gray-700 w-16">{s.typ === 'arbeit' ? 'Arbeit' : 'Pause'}</span>
                        <span className="text-gray-500">{fmtUhr(s.beginn)} – {s.ende ? fmtUhr(s.ende) : 'läuft'}</span>
                        {s.quelle === 'nachtrag' && <span className="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-600" title={s.grund || ''}>nachgetragen</span>}
                        <span className="ml-auto text-gray-400 tabular-nums">{fmtDauer(sessionMin(s))}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Nachtragen */}
                <div className="rounded-xl border border-gray-200">
                  <button onClick={() => { setNtOpen((o) => !o); if (!ntDatum) setNtDatum(ymd(today)); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-medium text-gray-700">
                    <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    Zeit nachtragen
                    <span className="text-[11px] text-gray-400 font-normal">(unterwegs gewesen / vergessen)</span>
                    <ChevronDown className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${ntOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {ntOpen && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="flex items-center gap-1.5">
                        <input type="date" value={ntDatum} onChange={(e) => setNtDatum(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                        <select value={ntTyp} onChange={(e) => setNtTyp(e.target.value as 'arbeit' | 'pause')} className="px-2 py-1.5 border border-gray-200 rounded-lg text-[13px] bg-white">
                          <option value="arbeit">Arbeit</option>
                          <option value="pause">Pause</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input type="time" value={ntVon} onChange={(e) => setNtVon(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                        <span className="text-gray-400 text-[13px]">–</span>
                        <input type="time" value={ntBis} onChange={(e) => setNtBis(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                      </div>
                      <input type="text" value={ntGrund} onChange={(e) => setNtGrund(e.target.value)} placeholder="Grund (Pflicht), z. B. Außentermin, vergessen einzustempeln …" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                      <button onClick={submitNachtrag} className="w-full py-2 rounded-lg bg-gray-900 text-white text-[13px] font-medium">Nachtragen</button>
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-100">
                  <p className="text-[12px] text-blue-900 leading-relaxed">
                    <span className="font-semibold">So einfach geht's:</span> Wenn du da bist, einmal auf <span className="font-semibold">Aktiv</span> — deine Zeit läuft.
                    Musst du zwischendurch weg (Friseur, Termin, nach Hause) oder machst Pause, stell dich auf <span className="font-semibold">Pause</span> bzw. <span className="font-semibold">Feierabend</span>.
                  </p>
                  <p className="text-[12px] text-blue-800/80 leading-relaxed mt-1.5">
                    Mal vergessen oder unterwegs gewesen? Kein Stress — einfach oben „Zeit nachtragen" mit kurzem Grund. Es geht nicht um Kontrolle 🙂, sondern um faire Stunden und darum, dass das Team weiß, wer erreichbar ist.
                  </p>
                </div>
              </div>
            )}

            {/* ===== TEAM ===== */}
            {tab === 'team' && (
              <div className="space-y-0.5">
                {members.map((m) => {
                  const st = (praesenz[m.id] || 'inaktiv') as Praesenz;
                  const u = urlaubAm(m.id, ymd(today));
                  const soll = sollHours(todayPlans[m.id]);
                  const online = onlineUserIds?.has(m.email);
                  const blink = scheduledNow(m.id) && st !== 'aktiv' && !u;
                  return (
                    <div key={m.id} className={`flex items-center gap-3 px-2 py-2 rounded-xl ${blink ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-[13px] font-semibold text-gray-600">
                          {(m.vorname?.[0] || '') + (m.nachname?.[0] || '')}
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${blink ? 'bg-red-500 animate-pulse' : dotColor(st)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] text-gray-900 truncate">{m.vorname} {m.nachname}</p>
                        {blink ? (
                          <p className="text-[12px] text-red-600 font-medium truncate">nicht aktiv seit {sinceText(praesenzSeit[m.id])}</p>
                        ) : (
                          <p className="text-[12px] text-gray-400 truncate">{u ? artLabel(u.art) : statusLabel(st)}{online && !u ? ' · online' : ''}</p>
                        )}
                      </div>
                      {u ? (
                        <span className="text-[12px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600">{artLabel(u.art)}</span>
                      ) : soll > 0 ? (
                        <span className="text-[12px] text-gray-400">{fmtH(soll)} Std</span>
                      ) : (
                        <span className="text-[12px] text-gray-300">frei</span>
                      )}
                    </div>
                  );
                })}
                {members.length === 0 && <p className="text-[12px] text-gray-400 px-2 py-2">Keine Mitarbeiter geladen.</p>}
              </div>
            )}

            {/* ===== WOCHE ===== */}
            {tab === 'woche' && (
              <div className="space-y-1.5">
                <p className="text-[12px] text-gray-500 px-1 mb-1">Deine Standard-Arbeitszeiten — jeder stellt selbst ein, wann er kommt und geht. Das ist dein Tages-Soll.</p>
                {myPlan.slice().sort((a, b) => a.wochentag - b.wochentag).map((r) => (
                  <div key={r.wochentag} className="flex items-center gap-2 px-1 py-0.5">
                    <button onClick={() => patchPlan(r.wochentag, { arbeitet: !r.arbeitet })} className={`w-10 text-[13px] font-medium rounded-lg py-1.5 shrink-0 ${r.arbeitet ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'}`}>{WT_KURZ[r.wochentag]}</button>
                    {r.arbeitet ? (
                      <>
                        <input type="time" value={r.von.slice(0, 5)} onChange={(e) => patchPlan(r.wochentag, { von: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                        <span className="text-gray-400 text-[13px]">–</span>
                        <input type="time" value={r.bis.slice(0, 5)} onChange={(e) => patchPlan(r.wochentag, { bis: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                        <div className="flex items-center gap-1 ml-auto">
                          <Coffee className="w-3.5 h-3.5 text-gray-300" />
                          <input type="number" min="0" step="15" value={r.pause_minuten} onChange={(e) => patchPlan(r.wochentag, { pause_minuten: Number(e.target.value) })} className="w-14 px-1.5 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                          <span className="text-[11px] text-gray-400">min</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-[13px] text-gray-300 flex-1">frei</span>
                    )}
                  </div>
                ))}
                <button onClick={savePlan} className="w-full mt-1.5 py-2 rounded-xl bg-gray-900 text-white text-[13px] font-medium">Wochenplan speichern</button>
              </div>
            )}

            {/* ===== URLAUB ===== */}
            {tab === 'urlaub' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setUrlaubView('meine')} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${urlaubView === 'meine' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Meine</button>
                    <button onClick={() => setUrlaubView('team')} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${urlaubView === 'team' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Team</button>
                  </div>
                  <span className="text-[12px] text-violet-600 font-medium">{urlaubView === 'meine' ? `${urlaubstageJahr} Urlaubstage ${jahr}` : `Jahr ${jahr}`}</span>
                </div>

                {urlaubView === 'team' && overlapTage > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[12px] text-amber-800">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    Achtung: an {overlapTage} Tagen überschneiden sich Urlaube — nur ein Hinweis zum Planen.
                  </div>
                )}

                <div className="space-y-1">
                  {MONATE.map((mon, m0) => {
                    const dim = daysInMonth(jahr, m0);
                    return (
                      <div key={m0} className="flex items-center gap-1.5">
                        <span className="w-7 text-[11px] text-gray-400 shrink-0">{mon}</span>
                        <div className="flex gap-[2px] flex-wrap">
                          {Array.from({ length: dim }, (_, i) => {
                            const d = new Date(jahr, m0, i + 1);
                            const ds = ymd(d);
                            const we = wtOf(d) >= 6;
                            const hol = holidays.has(ds);
                            const isToday = ds === ymd(today);
                            if (urlaubView === 'team') {
                              const list = urlaubMembersAm(ds);
                              const overlap = list.length > 1;
                              const baseCls = hol ? 'bg-orange-200' : we ? 'bg-gray-100' : 'bg-gray-200';
                              const title = list.length
                                ? list.map((u) => `${nameOf(u.mitarbeiter_id)} (${artLabel(u.art)})`).join(', ')
                                : `${i + 1}. ${mon}${hol ? ' · Feiertag' : ''}`;
                              return (
                                <span key={i} title={title}
                                  className={`w-2 h-3.5 rounded-[2px] ${list.length ? '' : baseCls} ${overlap ? 'ring-2 ring-red-500' : isToday ? 'ring-1 ring-gray-900' : ''}`}
                                  style={list.length ? { backgroundColor: colorFor(list[0].mitarbeiter_id) } : undefined} />
                              );
                            }
                            const u = urlaubAm(meId || '', ds);
                            const col = u ? (u.art === 'krank' ? 'bg-rose-400' : u.art === 'frei' ? 'bg-sky-300' : 'bg-violet-400') : hol ? 'bg-orange-200' : we ? 'bg-gray-100' : 'bg-gray-200';
                            return <span key={i} title={`${i + 1}. ${mon}${u ? ' · ' + artLabel(u.art) : hol ? ' · Feiertag' : ''}`} className={`w-2 h-3.5 rounded-[2px] ${col} ${isToday ? 'ring-1 ring-gray-900' : ''}`} />;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {urlaubView === 'meine' ? (
                  <div className="flex items-center gap-3 px-1 text-[11px] text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[2px] bg-violet-400" /> Urlaub</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[2px] bg-rose-400" /> Krank</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[2px] bg-sky-300" /> Frei</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[2px] bg-orange-200" /> Feiertag</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-x-3 gap-y-1 px-1 text-[11px] text-gray-500 flex-wrap">
                    {members.map((m) => (
                      <span key={m.id} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[2px]" style={{ backgroundColor: colorFor(m.id) }} /> {m.vorname}</span>
                    ))}
                    <span className="flex items-center gap-1 text-red-500"><span className="w-2.5 h-2.5 rounded-[2px] ring-2 ring-red-500" /> Überschneidung</span>
                  </div>
                )}
                {urlaubView === 'meine' ? (
                  <div className="space-y-1">
                    {myUrlaub.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50">
                        <Plane className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                        <span className="text-[13px] text-gray-700 flex-1">{fmtDatum(u.von_datum)} – {fmtDatum(u.bis_datum)} · {artLabel(u.art)}</span>
                        <button onClick={() => delUrlaub(u.id)} className="text-gray-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    {myUrlaub.length === 0 && <p className="text-[12px] text-gray-400 px-2 py-1">Noch nichts geplant.</p>}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {teamUrlaub.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50">
                        <span className="w-2.5 h-2.5 rounded-[2px] shrink-0" style={{ backgroundColor: colorFor(u.mitarbeiter_id) }} />
                        <span className="text-[13px] text-gray-700 flex-1 truncate">{nameOf(u.mitarbeiter_id)} · {fmtDatum(u.von_datum)} – {fmtDatum(u.bis_datum)} · {artLabel(u.art)}</span>
                      </div>
                    ))}
                    {teamUrlaub.length === 0 && <p className="text-[12px] text-gray-400 px-2 py-1">Keine anstehenden Urlaube.</p>}
                  </div>
                )}
                <div className="p-2.5 rounded-xl border border-gray-200 space-y-2">
                  <div className="flex items-center gap-1.5">
                    <input type="date" value={vonU} onChange={(e) => setVonU(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                    <span className="text-gray-400 text-[13px]">–</span>
                    <input type="date" value={bisU} onChange={(e) => setBisU(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px]" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <select value={artU} onChange={(e) => setArtU(e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[13px] bg-white">
                      <option value="urlaub">Urlaub</option>
                      <option value="krank">Krank</option>
                      <option value="frei">Frei</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                    <button onClick={addUrlaub} className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[13px] flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Eintragen</button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== AUSWERTUNG (nur Andreas) ===== */}
            {tab === 'auswertung' && isAndreas && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-gray-500" />
                  <span className="text-[13px] font-medium text-gray-700">Arbeitszeiten — nur für dich sichtbar</span>
                  <button onClick={exportCSV} className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-900 text-white text-[12px]"><Download className="w-3.5 h-3.5" />CSV</button>
                </div>
                <div className="flex items-center gap-1">
                  {(['heute', 'woche', 'monat'] as const).map((r) => (
                    <button key={r} onClick={() => { setAugRange(r); setSelMember(null); }} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${augRange === r ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                      {r === 'heute' ? 'Heute' : r === 'woche' ? 'Diese Woche' : 'Dieser Monat'}
                    </button>
                  ))}
                </div>
                <div className="space-y-0.5">
                  {members
                    .map((m) => ({ m, st: memberStats(m.id) }))
                    .sort((a, b) => b.st.ist - a.st.ist)
                    .map(({ m, st }) => {
                      const sel = selMember === m.id;
                      return (
                        <div key={m.id} className="rounded-lg">
                          <button onClick={() => setSelMember(sel ? null : m.id)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50">
                            <span className={`w-2 h-2 rounded-full ${dotColor((praesenz[m.id] || 'inaktiv') as Praesenz)}`} />
                            <span className="text-[13px] text-gray-800 flex-1 text-left">{m.vorname} {m.nachname}</span>
                            <span className="text-[12px] text-gray-400">Soll {fmtDauer(st.soll)}</span>
                            <span className="text-[13px] font-semibold text-gray-900 tabular-nums w-20 text-right">{fmtDauer(st.ist)}</span>
                            <span className={`text-[12px] font-medium w-20 text-right ${ampel(st.saldo)}`}>{fmtSaldo(st.saldo)}</span>
                          </button>
                          {sel && (
                            <div className="pl-3 pr-1 pb-2 space-y-0.5">
                              {st.rows.length === 0 && <p className="text-[12px] text-gray-400 px-2 py-1">Keine Zeiten im Zeitraum.</p>}
                              {st.rows.map((r) => (
                                <div key={r.ds} className="flex items-center gap-2 px-2 py-1 text-[12px]">
                                  <span className="w-20 text-gray-600">{fmtDatum(r.ds)}</span>
                                  <span className="text-gray-500 w-24">{r.kommen ? fmtUhr(r.kommen) : '—'} – {r.gehen ? fmtUhr(r.gehen) : (r.dIst > 0 ? 'läuft' : '—')}</span>
                                  {r.dPause > 0 && <span className="text-amber-600 text-[11px]">P {Math.round(r.dPause)}m</span>}
                                  {r.nachtrag && <span className="text-[10px] px-1 rounded bg-blue-50 text-blue-600" title={r.grund || ''}>NT</span>}
                                  <span className="ml-auto text-gray-700 tabular-nums">{fmtDauer(r.dIst)}</span>
                                  <span className={`w-16 text-right tabular-nums ${ampel(r.dIst - r.dSoll)}`}>{fmtSaldo(r.dIst - r.dSoll)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
                <p className="text-[11px] text-gray-400 px-1">Ist = gestempelt · Soll = Wochenplan (Feiertage NRW &amp; Urlaub/Krank zählen als 0). „NT" = nachgetragen. CSV ist direkt für den Steuerberater nutzbar.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
